/**
 * SplitContentCard - Two-column layout: image on one side, text on the other.
 * Set `flip={true}` to put the image on the right.
 */
import React from 'react';
import { assetUrl } from '../../lib/assets';

export interface SplitContentCardProps {
  title?: string;
  body?: string;
  content?: string;
  image?: string;
  image_alt?: string;
  flip?: boolean;
  className?: string;
}

const SplitContentCard: React.FC<SplitContentCardProps> = ({
  title,
  body,
  content,
  image,
  image_alt = '',
  flip = false,
  className = '',
}) => {
  const text = body || content || '';
  const imgCol = image ? (
    <div style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
      <img
        src={assetUrl(image)}
        alt={image_alt}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', aspectRatio: '4/3' }}
        loading="lazy"
      />
    </div>
  ) : (
    <div style={{
      borderRadius: 'var(--radius-xl)',
      backgroundColor: 'var(--color-surface-raised)',
      aspectRatio: '4/3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--color-text-muted)',
      fontSize: 'var(--font-size-sm)',
    }}>
      Image
    </div>
  );

  const textCol = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 'var(--spacing-4)' }}>
      {title && (
        <h2 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text)',
          lineHeight: 'var(--line-height-tight)',
        }}>
          {title}
        </h2>
      )}
      {text && (
        <div style={{ color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)', fontSize: 'var(--font-size-base)' }}>
          {text.split('\n\n').map((para, i) => (
            <p key={i} style={{ marginBottom: 'var(--spacing-4)' }}>{para.trim()}</p>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`split-content-card ${className}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--spacing-12)',
        alignItems: 'center',
        padding: 'var(--spacing-12) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {flip ? <>{textCol}{imgCol}</> : <>{imgCol}{textCol}</>}
    </div>
  );
};

export default SplitContentCard;
